// ContactModal.tsx
import React, {useCallback, useMemo, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {useTranslation} from "react-i18next";
import "./ContactModal.css";

// Интерфейс для данных формы
interface FormData {
    // type: 'MENU' | 'HIRE' | 'TARIFS',
    name: string,
    contacts: string,
    message: string,
}

const TG_BOT_TOKEN = '8959084449:AAFzIGkFZgKm1tntNhBQof-zvM_caUKiG5E';
const TG_CHAT_ID = '1187216345';

// ─── Form submit: прямой POST на Telegram Bot API, без серверной части.
//     Заявки летят в личку владельца бота @armpay_leads_bot.
//     Чтобы сменить бота/получателя — заменить TG_BOT_TOKEN и TG_CHAT_ID ниже.
//     (Создать своего бота: @BotFather в Telegram → /newbot. Свой chat_id: @userinfobot.)
async function sendTelegram(text: string, attempt?: number): Promise<boolean> {
    attempt = attempt || 1;
    // Browser fetch не имеет default timeout — зависает бесконечно.
    // AbortController обрезаем по 8 секунд на попытку, 3 попытки = 24с max.
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 8000);
    try {
        const res = await fetch(`https://api.telegram.org/bot${TG_BOT_TOKEN}/sendMessage`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({chat_id: TG_CHAT_ID, text: text, parse_mode: 'Markdown', disable_web_page_preview: true}),
            signal: controller.signal
        });
        clearTimeout(tid);
        const j = await res.json();
        if (j.ok) return true;
        throw new Error(j.description || 'TG error');
    } catch (e) {
        clearTimeout(tid);
        if (attempt < 3) {
            await new Promise(r => setTimeout(r, 500));
            return sendTelegram(text, attempt + 1);
        }
        throw e;
    }
}

// Интерфейс для пропсов модального окна
interface ContactModalProps {
    type: 'MENU' | 'HIRE' | 'TARIFS' | 'SUPPORT',
    show: boolean;           // управление видимостью
    onHide: () => void;      // функция закрытия
}

const ContactModal: React.FC<ContactModalProps> = ({type, show, onHide}) => {
    // Состояние формы
    const defaultInput = useMemo<FormData>(() => {
        return {name: '', contacts: '', message: ''}
    }, []);
    const [formData, setFormData] = useState<FormData>(defaultInput);

    // Состояние загрузки (для имитации отправки)
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Обработчик изменения полей
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };
    const {i18n, t} = useTranslation();
    const isYandex: boolean = i18n.language === "ru";

    // Обработка отправки формы
    const sendForm = useCallback(() => {
        setIsSubmitting(true);
        if (!formData.name.trim()) {
            alert(t("contacts.form.checks.name"));
            setIsSubmitting(false);
            return;
        }
        if (!formData.contacts.trim()) {
            alert(t("contacts.form.checks.contacts"));
            setIsSubmitting(false);
            return;
        }
        if (!formData.message.trim()) {
            alert(t("contacts.form.checks.message"));
            setIsSubmitting(false);
            return;
        }

        // Имитация отправки данных на сервер
        sendTelegram(`New message\n Type: ${type}\n Name: ${formData.name}\n Contacts: ${formData.contacts}\n Message: ${formData.message}\n`)
            .then(v => {
                console.log('Отправленные данные:', formData);
                // Успех – закрыть окно и сбросить форму
                onHide();
                setFormData(defaultInput);
            })
            .catch(error => {
                console.error('Ошибка отправки:', error);
                alert('Не удалось отправить сообщение. Попробуйте позже.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [type, formData, defaultInput, onHide, t]);

    // Сброс формы при закрытии окна (если не через отправку)
    const handleClose = () => {
        onHide();
        // Сбросить форму через небольшую задержку, чтобы не мешать анимации закрытия
        setTimeout(() => {
            setFormData(defaultInput);
        }, 300);
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("contacts.form.tittle")}</Modal.Title>
            </Modal.Header>

            {isYandex ?
                <Modal.Body
                    // className="responsive-iframe-body"
                    className="modal-dialog-scrollable"
                >
                    <script src="https://forms.yandex.ru/_static/embed.js"></script>
                    <iframe src="https://forms.yandex.ru/u/6a266b99493639575038f8cd?iframe=1" frameBorder="0" name="ya-form-6a266b99493639575038f8cd"
                            title="yandex_form_contacts"
                            width="100%" height={800}
                            // className="modal-iframe"
                    ></iframe>
                </Modal.Body> :
                <Form>
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="formName">
                            <Form.Label>{t("contacts.form.field.name")}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={t("contacts.form.field.name.placeholder")}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formPhone">
                            <Form.Label>{t("contacts.form.field.contacts")}</Form.Label>
                            <Form.Control
                                type="text"
                                name="contacts"
                                value={formData.contacts}
                                onChange={handleChange}
                                placeholder={t("contacts.form.field.contacts.placeholder")}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formMessage">
                            <Form.Label>{t("contacts.form.field.message")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={3}
                                placeholder={t("contacts.form.field.message.placeholder")}
                            />
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                            {t("button.close")}
                        </Button>
                        <Button variant="primary" disabled={isSubmitting} onClick={ignore => sendForm()}>
                            {isSubmitting ? t("button.sending") : t("button.send")}
                        </Button>
                    </Modal.Footer>
                </Form>
            }
        </Modal>
    );
};

export default ContactModal;
